import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui';
import { reportAPI, filenameFromResponse, reportErrorMessage } from '../../services/api';
import './ReportPdf.css';

/**
 * The WizeTok Interview Report PDF (backend app/services/reports): the one report
 * the app shows, wherever it is opened from (position Report modal, Reports page).
 *   - one exam:  useReportPdf({ sessionId })
 *   - combined:  useReportPdf({ campaignId, email })  (2+ exams, grouped by email)
 */
export function useReportPdf({ sessionId, campaignId, email }) {
  const combined = Boolean(campaignId && email);
  const [pdf, setPdf] = useState({ status: 'loading', url: null, filename: null, error: null });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    if (!combined && !sessionId) return undefined;
    let alive = true;
    let url = null;
    const refresh = refreshNonce > 0;
    setPdf({ status: 'loading', url: null, filename: null, error: null });
    const request = combined
      ? reportAPI.campaignPdf(campaignId, email, refresh)
      : reportAPI.sessionPdf(sessionId, refresh);
    request
      .then((res) => {
        url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const filename = filenameFromResponse(res, combined ? 'WizeTok_Campaign_Report.pdf' : 'WizeTok_Report.pdf');
        if (alive) setPdf({ status: 'ready', url, filename, error: null });
      })
      .catch(async (err) => {
        const message = await reportErrorMessage(err);
        if (alive) setPdf({ status: 'error', url: null, filename: null, error: message });
      });
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [combined, campaignId, email, sessionId, refreshNonce]);

  const regenerate = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const download = useCallback(() => {
    if (!pdf.url) return;
    const a = document.createElement('a');
    a.href = pdf.url;
    a.download = pdf.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [pdf]);

  return { pdf, regenerate, download };
}

/** Regenerate + Download PDF buttons for a useReportPdf() result. */
export function ReportPdfActions({ pdf, regenerate, download }) {
  return (
    <>
      <Button small icon="refresh" disabled={pdf.status === 'loading'} title="Rewrite the report text and render it again"
        onClick={regenerate}>Regenerate</Button>
      <Button small variant="primary" icon="download" disabled={pdf.status !== 'ready'} onClick={download}>Download PDF</Button>
    </>
  );
}

/** The PDF preview, or a loading / error message in its place. */
export function ReportPdfBody({ pdf, title }) {
  return (
    <div className="wz-report-body">
      {pdf.status === 'loading' && (
        <div className="wz-report-msg">
          <p style={{ fontWeight: 600 }}>Preparing the report…</p>
          <p className="wz-sub">The first report for a session takes a few seconds while the summary is written. After that it opens instantly.</p>
        </div>
      )}
      {pdf.status === 'error' && (
        <div className="wz-report-msg">
          <p style={{ fontWeight: 600 }}>The report could not be prepared.</p>
          <p className="wz-sub">{pdf.error}</p>
        </div>
      )}
      {pdf.status === 'ready' && (
        <iframe title={title} src={pdf.url} className="wz-report-frame" />
      )}
    </div>
  );
}
